import { cn } from '@amore/ui/cn'
import { Avatar } from '@amore/ui/components/avatar'
import { Badge } from '@amore/ui/components/badge'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { formatDate, isOverdue, relativeDue } from '@/lib/format'
import { priorityLabels } from '@/lib/labels'
import { useWedding } from '@/mock/store'
import type { Task } from '@/mock/types'
import { type Column, DataTable } from '@/patterns/data-table'
import { ScreenSection } from '@/patterns/screen'
import { taskSource } from '../tasks/model'
import { priorityTasks } from './model'

export function OverviewTasks() {
  const data = useWedding()
  const navigate = useNavigate()
  const members = new Map(data.members.map((member) => [member.id, member]))
  const columns: Column<Task>[] = [
    {
      key: 'title',
      header: 'Úkol',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (task) => {
        const source = taskSource(task, data)
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="font-medium">{task.title}</span>
            {source ? (
              <span className="text-xs font-normal text-muted-foreground">{source.line}</span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'priority',
      header: 'Priorita',
      width: '7rem',
      cell: (task) => (
        <Badge tone={task.priority === 'high' ? 'danger' : 'neutral'}>
          {priorityLabels[task.priority]}
        </Badge>
      ),
    },
    {
      key: 'date',
      header: 'Termín',
      width: '10rem',
      cell: (task) =>
        task.dueDate ? (
          <span className="flex flex-col gap-1">
            <span
              className={cn(
                'text-sm',
                isOverdue(task.dueDate) && 'font-semibold text-danger-foreground',
              )}
            >
              {relativeDue(task.dueDate)}
            </span>
            <span className="text-xs text-muted-foreground">{formatDate(task.dueDate)}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Bez termínu</span>
        ),
    },
    {
      key: 'owner',
      header: 'Kdo řeší',
      width: '11rem',
      cell: (task) => {
        const member = task.assigneeId ? members.get(task.assigneeId) : undefined
        return member ? (
          <span className="flex min-w-0 items-center gap-2">
            <Avatar
              size="sm"
              alt=""
              fallback={member.initials}
              className="hidden size-7 md:block"
            />
            <span className="truncate">{member.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Bez přiřazení</span>
        )
      },
    },
  ]
  return (
    <ScreenSection
      title="Úkoly, kterými začít"
      description="Pět nejdůležitějších otevřených úkolů. Po termínu, podle priority a nejbližšího data."
      action={
        <Link
          to="/tasks"
          search={{ filter: 'open' }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-action hover:underline"
        >
          Všechny úkoly
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      }
    >
      <div className="[&_th]:text-foreground/80">
        <DataTable
          rows={priorityTasks(data)}
          columns={columns}
          getRowId={(task) => task.id}
          onRowClick={(task) => void navigate({ to: `/tasks/${task.id}` })}
          rowTone={(task) => (isOverdue(task.dueDate) ? 'danger' : undefined)}
          empty="Všechny úkoly máte hotové. Další se tu objeví, až je přidáte."
          aria-label="Pět prioritních úkolů"
        />
      </div>
    </ScreenSection>
  )
}
